export type Lang = 'ar' | 'en';

/**
 * الأرقام لاتينية (1234) لا هندية (١٢٣٤)، حتى في الواجهة العربية.
 *
 * `ar-JO` وحدها تعطي الأرقام الهندية، وهي صحيحة لغوياً لكنها ليست ما يقرأه
 * الأردنيون في الفواتير ولوحات المفاتيح وتطبيقات البنوك. واختلاطها مع
 * الأرقام اللاتينية في الصفحة نفسها — كما كان يحدث في المبالغ مقابل
 * الأعداد — أسوأ من أي منهما وحده.
 *
 * اللاحقة `-u-nu-latn` تغيّر نظام الأرقام فقط: أسماء الشهور وترتيب التاريخ
 * والفاصلة العشرية تبقى عربية.
 */
export const locale = (language: Lang): string =>
  language === 'ar' ? 'ar-JO-u-nu-latn' : 'en-JO';

/** JOD is stored as a 3dp decimal string. Internally we work in fils (1 JOD = 1000 fils). */
export function toFils(amount: string | number): number {
  if (typeof amount === 'number') return Math.round(amount * 1000);
  const clean = amount.trim().replace(/[^\d.-]/g, '');
  if (!clean) return 0;
  const negative = clean.startsWith('-');
  const [whole, fraction = ''] = clean.replace('-', '').split('.');
  const fils = Number(whole || '0') * 1000 + Number((fraction + '000').slice(0, 3));
  return negative ? -fils : fils;
}

export function fromFils(fils: number): string {
  const negative = fils < 0;
  const abs = Math.abs(Math.round(fils));
  const value = `${Math.floor(abs / 1000)}.${String(abs % 1000).padStart(3, '0')}`;
  return negative ? `-${value}` : value;
}

export function addMoney(...amounts: string[]): string {
  return fromFils(amounts.reduce((sum, a) => sum + toFils(a), 0));
}

export function subtractMoney(a: string, b: string): string {
  return fromFils(toFils(a) - toFils(b));
}

export function normalizeMoney(input: string): string {
  return fromFils(toFils(input));
}

export function isValidMoney(input: string): boolean {
  return /^\d+(\.\d{1,3})?$/.test(input.trim());
}

/** Always exactly three fraction digits, with the localized currency suffix. */
/**
 * عملة العمارة النشطة.
 *
 * تُضبط مرّة عند تحميل الجلسة وتُقرأ من كل نداء لـ`formatMoney`. والبديل
 * تمرير العملة إلى مئة موضع استدعاء — وأيّ موضع يُنسى يعرض مبلغاً بعملة
 * عمارة أخرى، وهو خطأ لا يُرى لأنه لا يُعطّل شيئاً.
 *
 * والتخزين يبقى بثلاث خانات دائماً (`NUMERIC(12,3)` في القاعدة): ثلاث
 * خانات تسع عملة بخانتين بلا فقد، والعرض وحده يتبع العملة.
 */
let activeCurrency = { code: 'JOD', minorUnits: 3 };

export function setActiveCurrency(code: string, minorUnits: number): void {
  activeCurrency = {
    code: (code || 'JOD').toUpperCase(),
    minorUnits: minorUnits >= 0 && minorUnits <= 3 ? minorUnits : 3,
  };
}

export function getActiveCurrency(): { code: string; minorUnits: number } {
  return activeCurrency;
}

/** رمز العملة كما يُعرض. العربية لها اختصاراتها المألوفة. */
const SYMBOLS_AR: Record<string, string> = {
  JOD: 'د.أ', KWD: 'د.ك', BHD: 'د.ب', OMR: 'ر.ع',
  USD: '$', EUR: '€', SAR: 'ر.س', AED: 'د.إ',
  EGP: 'ج.م', ILS: '₪', GBP: '£',
};

export function currencySymbol(code: string, language: Lang): string {
  if (language === 'ar') return SYMBOLS_AR[code] ?? code;
  return code;
}

export function formatMoney(amount: string, language: Lang): string {
  const fils = toFils(amount);
  const negative = fils < 0;
  const abs = Math.abs(fils);
  const whole = Math.floor(abs / 1000);
  const grouped = new Intl.NumberFormat(locale(language), {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(whole);

  // الكسر يُقتطع لا يُقرّب.
  //
  //  المبلغ مخزَّن بثلاث خانات دائماً. عملة بخانتين تعرض أوّلَ خانتين —
  //  والتقريب هنا يجعل 25.999 تظهر 26.00 فتختلف عن مجموعٍ حُسب في
  //  القاعدة. العرض لا يغيّر الرقم.
  const { code, minorUnits } = activeCurrency;
  const fraction = String(abs % 1000).padStart(3, '0').slice(0, minorUnits);

  // الخانات تُطبع كما هي: تحويلها إلى أرقام هندية كان يجعل المبلغ الواحد
  // يحمل نظامي ترقيم في آن — «1,240.٥٠٠».
  const suffix = currencySymbol(code, language);
  const decimals = minorUnits > 0 ? `.${fraction}` : '';
  return `${negative ? '−' : ''}${grouped}${decimals} ${suffix}`;
}

export function formatNumber(value: number, language: Lang): string {
  return new Intl.NumberFormat(locale(language)).format(value);
}

export function formatPercent(ratio: number, language: Lang): string {
  return new Intl.NumberFormat(locale(language), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(ratio);
}

/** API timestamps are UTC ISO-8601; the UI always displays Asia/Amman. */
export function formatDate(iso: string, language: Lang): string {
  return new Intl.DateTimeFormat(locale(language), {
    timeZone: 'Asia/Amman',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string, language: Lang): string {
  return new Intl.DateTimeFormat(locale(language), {
    timeZone: 'Asia/Amman',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * السنة رقم تعريفي لا كمّية.
 *
 * `formatNumber(2026)` يعطي «2,026» لأن `Intl.NumberFormat` يجمّع الآلاف
 * — وهو صحيح للمبالغ وخاطئ تماماً للسنوات. فاصلة في سنة تجعلها تُقرأ
 * كرقم مالي للحظة، وهذا وحده يكفي.
 */
export function formatYear(year: number, language: Lang): string {
  return new Intl.NumberFormat(locale(language), { useGrouping: false }).format(year);
}

/**
 * اسم الشهر وحده، بلا سنة.
 *
 * ولماذا دالة بدل قصّ ناتج `formatMonth` عند أول مسافة: لأن نصف الشهور
 * العربية اسمها كلمتان — «كانون الثاني» و«تشرين الأول» — فالقصّ يعطي
 * «كانون» و«تشرين» مرتين في السنة الواحدة، ويصير في الشبكة مربّعان
 * متطابقان لا يُفرَّق بينهما.
 */
export function formatMonthName(year: number, month: number, language: Lang): string {
  return new Intl.DateTimeFormat(locale(language), {
    timeZone: 'Asia/Amman',
    month: 'long',
  }).format(new Date(Date.UTC(year, month - 1, 15)));
}

export function formatMonth(year: number, month: number, language: Lang): string {
  return new Intl.DateTimeFormat(locale(language), {
    timeZone: 'Asia/Amman',
    month: 'long',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, 15)));
}

/** أي صيغة -> رقم دولي بلا `+`؛ الأردن افتراضي لما بدأ بصفر أو بسبعة (#89). */
export function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const bare = digits.replace(/^00/, '');
  if (bare.startsWith('962')) return bare;
  if (bare.startsWith('0')) return `962${bare.replace(/^0+/, '')}`;
  if (/^7\d{8}$/.test(bare)) return `962${bare}`;
  return bare;
  return digits;
}

export function formatPhone(phone: string): string {
  const n = normalizePhone(phone);
  // الأردني بالتجميع المألوف؛ وغيره `+رمز الدولة` ثم الباقي بمجموعات
  // ثلاثية من اليمين — مقروء لأي دولة بلا جدول رموز (#89).
  if (/^9627\d{8}$/.test(n)) return `+962 ${n.slice(3, 4)} ${n.slice(4, 8)} ${n.slice(8)}`;
  if (!/^\d{8,15}$/.test(n)) return phone;
  const cc = n.startsWith('971') || n.startsWith('966') || n.startsWith('963')
    || n.startsWith('964') || n.startsWith('965') || n.startsWith('968')
    || n.startsWith('970') || n.startsWith('973') || n.startsWith('974')
    || n.startsWith('249') || n.startsWith('212') ? 3 : n.startsWith('20') || n.startsWith('90') || n.startsWith('44') || n.startsWith('49') ? 2 : 1;
  const rest = n.slice(cc).replace(/(\d{3})(?=\d)/g, '$1 ');
  return `+${n.slice(0, cc)} ${rest}`;
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('');
}

export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}
